import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ComuneInput, StreetInput } from "@/components/booking/AddressFields";
import { LocationPicker } from "@/components/booking/LocationPicker";
import { ResendConfirmAsk } from "@/components/ResendConfirmAsk";
import { SecureGate } from "@/components/SecureGate";
import { useAuth } from "@/lib/auth";

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

const Guard = ({ on, children }) => (on
  ? <SecureGate description="Inserisci la seconda password per modificare un intervento già completato. Resta sbloccata per 30 minuti.">{children}</SecureGate>
  : children);

export function EditJobDialog({ job, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!open || !job) return;
    const c = job.client;
    setF({ first_name: c.first_name, last_name: c.last_name, email: c.email, phone: c.phone, address: c.address, city: c.city, tax_code: c.tax_code || "",
      lat: job.location?.coordinates[1] ?? null, lng: job.location?.coordinates[0] ?? null,
      equipment_type: job.equipment_type || "", brand_model: job.brand_model || "", notes: job.notes || "",
      date: job.date, start: job.start, end: job.end, end_date: job.end_date || "", summary: job.closure?.summary || "" });
    api.get("/public/config", { params: { all: true } }).then((r) => setEquipment(r.data.equipment));
  }, [open, job]);

  if (!f || !job) return null;
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pending = job.status === "in_attesa";
  const done = job.status === "completato" && user?.role === "admin";
  const open_ = done || ["in_attesa", "confermato"].includes(job.status);
  const extendable = !job.series_id && ["in_attesa", "confermato", "in_corso"].includes(job.status)
    && (!!job.end_date || !!equipment.find((e) => e.key === (f.equipment_type || job.equipment_type))?.multi_day);

  const confirmed = job.status === "confermato" && !!job.technician_id;
  const save = async (notify_client) => {
    setAsk(false);
    setBusy(true);
    try {
      const body = { ...f, equipment_type: f.equipment_type || null, tax_code: f.tax_code.trim().toUpperCase() };
      if (confirmed) body.notify_client = notify_client;
      if (!open_ || job.series_id) ["date", "start", "end"].forEach((k) => delete body[k]);
      if (!open_) ["equipment_type", "brand_model", "notes"].forEach((k) => delete body[k]);
      if (done) delete body.equipment_type;
      if (!done || !job.closure) delete body.summary;
      if (!extendable || (f.end_date || "") === (job.end_date || "")) delete body.end_date;
      else body.end_date = f.end_date || null;
      const { data } = await api.patch(`/admin/jobs/${job.id}`, body);
      toast.success("Dati aggiornati");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-job-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{done ? "Modifica intervento completato" : open_ ? "Modifica richiesta" : "Modifica dati cliente"}</DialogTitle></DialogHeader>
        <Guard on={done}>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Nome"><Input value={f.first_name} onChange={upd("first_name")} data-testid="edit-first-name" /></F>
          <F label="Cognome"><Input value={f.last_name} onChange={upd("last_name")} data-testid="edit-last-name" /></F>
          <F label="Email"><Input type="email" value={f.email} onChange={upd("email")} data-testid="edit-email" /></F>
          <F label="Telefono"><Input value={f.phone} onChange={upd("phone")} data-testid="edit-phone" /></F>
          <F label="Codice fiscale / P. IVA (facoltativo)" className="sm:col-span-2"><Input value={f.tax_code} maxLength={16} className="font-mono uppercase"
            onChange={(e) => setF((p) => ({ ...p, tax_code: e.target.value.toUpperCase().replace(/\s/g, "") }))} data-testid="edit-tax-code" /></F>
          <F label="Comune"><ComuneInput value={f.city} onChange={(v) => setF((p) => ({ ...p, city: v }))} onSelect={(c) => setF((p) => ({ ...p, city: c.name }))} /></F>
          <F label="Indirizzo"><StreetInput value={f.address} comune={f.city} onChange={(v) => setF((p) => ({ ...p, address: v }))}
            onPick={(s) => setF((p) => ({ ...p, address: [s.street || s.name, s.housenumber].filter(Boolean).join(" "), city: s.city || p.city, lat: s.lat, lng: s.lng }))} /></F>
          <div className="space-y-2 sm:col-span-2"><Label>Posizione sulla mappa</Label>
            <LocationPicker lat={f.lat} lng={f.lng} address={f.address} city={f.city} onChange={({ geo_source, ...patch }) => setF((p) => ({ ...p, ...patch }))} /></div>
          {extendable && (
            <F label="Data fine (proroga se non si finisce in tempo)" className="sm:col-span-2">
              <Input type="date" min={f.date} value={f.end_date} onChange={upd("end_date")} data-testid="edit-end-date" /></F>
          )}
          {open_ && !done && job.kind !== "sopralluogo" && (
            <F label="Tipo di intervento">
              <Select value={f.equipment_type || "none"} onValueChange={(v) => setF((p) => ({ ...p, equipment_type: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="edit-equipment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {equipment.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          )}
          {open_ && <F label="Marca / Modello"><Input value={f.brand_model} onChange={upd("brand_model")} data-testid="edit-brand-model" /></F>}
          {open_ && <F label="Note" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={upd("notes")} data-testid="edit-notes" /></F>}
          {done && job.closure && <F label="Descrizione lavori svolti (rapporto)" className="sm:col-span-2"><Textarea rows={4} value={f.summary} onChange={upd("summary")} data-testid="edit-summary" /></F>}
          {open_ && !job.series_id && (<>
            <div className="grid grid-cols-3 gap-3 sm:col-span-2">
              <F label="Data"><Input type="date" value={f.date} onChange={upd("date")} data-testid="edit-date" /></F>
              <F label="Dalle"><Input type="time" value={f.start} onChange={upd("start")} data-testid="edit-start" /></F>
              <F label="Alle"><Input type="time" value={f.end} onChange={upd("end")} data-testid="edit-end" /></F>
            </div>
            {!pending && !done && job.technician_id && <p className="text-xs text-slate-500 sm:col-span-2" data-testid="edit-move-hint">Il tecnico ({job.technician_name}) resta lo stesso e riceve la notifica; al salvataggio ti chiediamo se inviare la nuova conferma al cliente{job.tech_ack ? " (parte solo dopo la conferma del tecnico esterno)" : ""}.</p>}
          </>)}
        </div>
        <DialogFooter><Button onClick={() => (confirmed ? setAsk(true) : save())} disabled={busy} className="rounded-full" data-testid="edit-job-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva modifiche</Button></DialogFooter>
        </Guard>
        <ResendConfirmAsk open={ask} onCancel={() => setAsk(false)} onAnswer={save} external={!!job.tech_ack} />
      </DialogContent>
    </Dialog>
  );
}
